/* eslint-disable no-lonely-if */
import React from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import classnames from 'classnames';
import { useTranslations } from 'next-intl';
import { usePathname, useSearchParams } from 'next/navigation';
import Typography from '../Typography';
import paginationConfig from '../../utils/pagination';

const Pagination = ({
  hasNextPage = false,
  hasPrevPage = false,
  selectedPage = null,
  showAllProducts = false,
  totalPages = null,
}) => {
  // translations
  const t = useTranslations('Pagination');

  // get pathname
  const pathname = usePathname();

  // get search params
  const searchParams = new URLSearchParams(useSearchParams().toString());

  // middle
  let startNum = selectedPage - 1;
  let endNum = selectedPage + 2;

  if (totalPages <= 4) {
    // 4 or less pages, show them all
    startNum = 1;
    endNum = totalPages;
  } else {
    // more than 4, show 4 at a time
    if (selectedPage <= 3) {
      // beginning
      startNum = 1;
      endNum = 4;
    } else if (selectedPage + 2 >= totalPages) {
      // end
      startNum = totalPages - 3;
      endNum = totalPages;
    }
  }

  const length = endNum - startNum + 1;
  const pages = Array.from({ length }, (_, i) => { return i + startNum; });

  const getPageLink = (page) => {
    if (page === 1) {
      searchParams.delete('page');
    } else {
      searchParams.set('page', page);
    }
    return `${pathname}?${searchParams.toString()}`;
  };

  const getPrevLink = () => {
    if (selectedPage === 2) {
      searchParams.delete('page');
    } else {
      searchParams.set('page', selectedPage - 1);
    }
    return `${pathname}?${searchParams.toString()}`;
  };

  const getNextLink = () => {
    searchParams.set('page', selectedPage + 1);
    return `${pathname}?${searchParams.toString()}`;
  };

  const getShowMoreLink = () => {
    searchParams.set('page', paginationConfig.allProductsParam);
    return `${pathname}?${searchParams.toString()}`;
  };

  const getShowLessLink = () => {
    searchParams.delete('page');
    return `${pathname}?${searchParams.toString()}`;
  };

  const prevBtn = (
    <div className="pagination-chev pagination-chev-prev" key="prev-btn">
      <Link
        href={getPrevLink()}
        data-content-name="browse-pagingation"
        data-content-piece={`browse pagination previous button page ${selectedPage - 1}`}
        prefetch={false}
        aria-label={t('nav.previous.ariaLabel')}
      >
        <svg viewBox="0 0 14 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M5.514 12 14 21.074l-2.424 2.593L.666 12 11.577.333 14 2.925 5.514 12z" fill="#3F385A" />
        </svg>
      </Link>
    </div>
  );

  // don't use links on disabled buttons
  const disabledPrevBtn = (
    <div className="pagination-chev pagination-chev-prev" key="disabled-prev-btn">
      <span className="pagination-chev-disabled">
        <svg viewBox="0 0 14 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M5.514 12 14 21.074l-2.424 2.593L.666 12 11.577.333 14 2.925 5.514 12z" fill="#3F385A" />
        </svg>
      </span>
    </div>
  );

  const pageButtons = pages && pages.map((page) => {
    return (
      <div className="pagination-item" key={`pagination-${page.toString()}`}>
        <Link
          href={getPageLink(page)}
          data-content-name="browse-pagingation"
          data-content-piece={`browse pagination page ${page}`}
          prefetch={false}
          className={classnames('pagination-link', { 'pagination-link-selected': selectedPage === page })}
        >
          <Typography variant="body-bold">{page.toString()}</Typography>
        </Link>
      </div>
    );
  });

  const nextBtn = (
    <div className="pagination-chev pagination-chev-next" key="next-btn">
      <Link
        href={getNextLink()}
        data-content-name="browse-pagingation"
        data-content-piece={`browse pagination next button page ${selectedPage + 1}`}
        prefetch={false}
        aria-label={t('nav.next.ariaLabel')}
      >
        <svg viewBox="0 0 14 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M8.485 12 0 2.925 2.424.333 13.334 12 2.423 23.667 0 21.074 8.485 12Z" fill="#3F385A" />
        </svg>
      </Link>
    </div>
  );

  // don't use links on disabled buttons
  const disabledNextBtn = (
    <div className="pagination-chev pagination-chev-next" key="disabled-next-btn">
      <span className="pagination-chev-disabled">
        <svg viewBox="0 0 14 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M8.485 12 0 2.925 2.424.333 13.334 12 2.423 23.667 0 21.074 8.485 12Z" fill="#3F385A" />
        </svg>
      </span>
    </div>
  );

  const showMoreBtn = (
    <Link href={getShowMoreLink()} prefetch={false} className="pagination-more-or-less-btn button-secondary" role="button">
      {t('cta.all')}
    </Link>
  );

  const showLessBtn = (
    <Link href={getShowLessLink()} prefetch={false} className="pagination-more-or-less-btn button-secondary" role="button">
      {t('cta.less')}
    </Link>
  );

  // next and previous buttons
  const prevBtnToDisplay = hasPrevPage ? prevBtn : disabledPrevBtn;
  const nextBtnToDisplay = hasNextPage ? nextBtn : disabledNextBtn;

  // display nothing, or 'show less' button, or 'show more' button
  let showBtnToDisplay = null;
  if (showAllProducts) {
    showBtnToDisplay = showLessBtn;
  } else if (totalPages > 1) {
    showBtnToDisplay = showMoreBtn;
  }

  return (
    <div className="pagination">
      <div className="pagination-nav">
        {[prevBtnToDisplay, pageButtons, nextBtnToDisplay]}
      </div>
      {showBtnToDisplay && (
        <div className="pagination-load">{showBtnToDisplay}</div>
      )}
    </div>
  );
};

Pagination.propTypes = {
  selectedPage: PropTypes.number,
  showAllProducts: PropTypes.bool,
  totalPages: PropTypes.number,
  hasPrevPage: PropTypes.bool,
  hasNextPage: PropTypes.bool,
};

export default Pagination;
